// deps
import cx from "classnames";
import React, { ReactElement, memo, useMemo, ReactNode } from "react";

// internal-deps
import { CartRestaurantSuggestion } from "@swiggy-private/cart-ui";
import { Image } from "@swiggy-private/core-ui";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";

// hooks
import { UseRxInstructionReturnType } from "@cart-client/hooks/useRxInstructions";

// styles
import styles from "./RxSpecialInstructions.scss";

// constants
import { RX_INSTRUCTIONS_ASSETS } from "@cart-client/constants/RxInstructions";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { BottomInputHalfCardContent } from "@swiggy-private/cart-ui";

export interface RxInstructionProps extends UseRxInstructionReturnType {
    specialInstruction: string;
    placeholder?: string;
    title?: string;
    onCookingInstructionsFocus?: () => void;
    onCookingInstructionsBlur?: () => void;
    onCookingInstructionsClick?: (element: HTMLElement) => void;
}

function RxSpecialInstructions({
    specialInstruction,
    placeholder,
    onSpecialInstructionsClick,
    onSpecialInstructionsImpression,
    handleRestaurantSuggestionChange,
    onRxInstructionClick,
    onToastDismiss,
    showRxHalfCard,
    isOptedInRx,
    isRxInstructionsHalfCardXPEnabled,
    rxHalfcardProps,
    toastData,
    isTimerOrCroutonAvailable,
    note,
    textAreaActive,
    onButtonClick,
    title,
    onCookingInstructionsFocus,
    onCookingInstructionsBlur,
    onCookingInstructionsClick,
}: RxInstructionProps): ReactElement {
    const { isOpen, onClose, ...contentProps } = rxHalfcardProps;
    const rxToast = useMemo(() => {
        if (!toastData.show) {
            return null;
        }
        const toastContainerClass = cx(styles.toastContainer, {
            [styles.toastContainerWithExtraBottom]: isTimerOrCroutonAvailable,
        });
        const toastIconClass = cx(styles.toastIcon, {
            [styles.toastIconDelete]: toastData.icon === RX_INSTRUCTIONS_ASSETS.TOAST_DELETE,
        });
        const toastIcon: ReactNode = (
            <div className={styles.toastIconWrapper}>
                <Image className={toastIconClass} imageId={toastData.icon} />
            </div>
        );

        return (
            <Toast
                message={toastData.message}
                leftAsset={toastIcon}
                onDismiss={onToastDismiss}
                containerClass={toastContainerClass}
                autoDismiss
                show
            />
        );
    }, [isTimerOrCroutonAvailable, onToastDismiss, toastData]);

    return (
        <>
            <CartRestaurantSuggestion
                restaurantSuggestion={specialInstruction}
                inputChangeCallback={handleRestaurantSuggestionChange}
                onImpression={onSpecialInstructionsImpression}
                onClick={onSpecialInstructionsClick}
                isOptedInRx={isOptedInRx}
                placeholder={placeholder}
                note={note}
                textAreaActive={textAreaActive}
                onButtonClick={isRxInstructionsHalfCardXPEnabled ? onRxInstructionClick : onButtonClick}
                title={title}
                isRxInstructionsHalfCardXPEnabled={isRxInstructionsHalfCardXPEnabled}
                onCookingInstructionsFocus={onCookingInstructionsFocus}
                onCookingInstructionsBlur={onCookingInstructionsBlur}
                onCookingInstructionsClick={onCookingInstructionsClick}
            />
            {isRxInstructionsHalfCardXPEnabled && (
                <>
                    {showRxHalfCard && (
                        <HalfCard isOpen={isOpen} onClose={onClose} variant="v3">
                            <BottomInputHalfCardContent {...contentProps} />
                        </HalfCard>
                    )}
                    {rxToast}
                </>
            )}
        </>
    );
}

export default memo(RxSpecialInstructions);
